import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import api from "../../api/api"; // Axios instance for API calls
import { PaginationInfo } from "../../types/api";

interface TaxTypesContextType {
  taxTypes: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchTaxTypes: (page?: number, perPage?: number) => Promise<void>;
  addTaxType: (tax: any) => Promise<void>;
  editTaxType: (updatedTax: any) => Promise<void>;
  deleteTaxType: (id: number | string) => Promise<void>;
}

export const TaxTypesContext = createContext<TaxTypesContextType | undefined>(
  undefined,
);

interface TaxTypesProviderProps {
  children: ReactNode;
}

export const useTaxTypesContext = (): TaxTypesContextType => {
  const context = useContext(TaxTypesContext);
  if (!context) {
    throw new Error("useTaxTypesContext must be used within TaxTypesProvider");
  }
  return context;
};

const TaxTypesProvider: React.FC<TaxTypesProviderProps> = ({ children }) => {
  const [taxTypes, setTaxTypes] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch all tax types with pagination
  const fetchTaxTypes = useCallback(async (page: number = 1, perPage: number = 15) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/tax-types?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let taxTypesData = [];
      let paginationData = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          // Nested structure: { success: true, data: { data: [...], pagination: {...} } }
          taxTypesData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          // Direct array structure: { success: true, data: [...], pagination: {...} }
          taxTypesData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          taxTypesData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        taxTypesData = responseData.data;
        paginationData = responseData.pagination;
      } else if (Array.isArray(responseData)) {
        taxTypesData = responseData;
      }
      
      setTaxTypes(taxTypesData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch tax types:", error);
      setTaxTypes([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new tax type
  const addTaxType = async (tax: any): Promise<void> => {
    try {
      // totalGst is derived server-side (cgst+sgst+igst); don't send it.
      const response = await api.post("/tax-types", { ...tax });
      const newTaxType = response.data.data || response.data;
      setTaxTypes((prevTaxTypes) => [...prevTaxTypes, newTaxType]);
    } catch (error) {
      console.error("Failed to add tax type:", error);
      throw error;
    }
  };

  // Edit an existing tax type
  const editTaxType = async (updatedTax: any): Promise<void> => {
    try {
      const response = await api.put(`/tax-types/${updatedTax.id}`, {
        ...updatedTax,
      });

      const updatedTaxType = response.data.data || response.data;
      setTaxTypes((prevTaxTypes) =>
        prevTaxTypes.map((tax) =>
          tax.id === updatedTax.id ? updatedTaxType : tax
        )
      );
    } catch (error) {
      console.error("Failed to edit tax type:", error);
      throw error;
    }
  };

  // Delete a tax type
  const deleteTaxType = async (id: number | string): Promise<void> => {
    try {
      await api.delete(`/tax-types/${id}`);
      setTaxTypes((prevTaxTypes) => prevTaxTypes.filter((tax) => tax.id !== id));
    } catch (error) {
      console.error("Failed to delete tax type:", error);
      throw error;
    }
  };

  // REMOVED auto-fetch useEffect - components will call fetchTaxTypes explicitly with pagination

  return (
    <TaxTypesContext.Provider
      value={{ taxTypes, pagination, loading, fetchTaxTypes, addTaxType, editTaxType, deleteTaxType }}
    >
      {children}
    </TaxTypesContext.Provider>
  );
};

export default TaxTypesProvider;
